import { useQuery, UseQueryOptions } from "@oysterhr/core"
import * as Yup from "yup"
import { OperationVariables, gql } from "@apollo/client"
import { Money, SalaryFrequencyEnumType, TaxContribution, VatParameters } from "@oysterhr/schema"
import { getVariableCompensationValue } from "~/shared/components/VariableCompensationSummary/helpers"
import {
  getBenefitFeeValue,
  sumPlanPrices,
  ESSENTIAL_PACKAGE_NAME,
  HEALTH_CATEGORY,
  sumPlanFees,
} from "@oysterhr/benefits"
import {
  BENEFIT_GROUP,
  MONTHS_IN_A_YEAR,
  RADIO_LABEL_NO,
  RADIO_LABEL_YES,
} from "../utils/constants"
import type { TFunction } from "react-i18next"
import {
  BenefitsDetails,
  BenefitsGroup,
  CalculationQueryFields,
  CountryFormData,
  EmployerTaxBenefit,
  QuoteCycle,
  SalaryInput,
  VariableCompensationsGroup,
} from "../types"

export const getCycleProratedAmount = (amount: number, cycle: QuoteCycle) =>
  Math.round(amount / (cycle === "annual" ? 1 : 12))

export const getAmountWithCurrencyAndCommas = (
  amount: number,
  currencyCode: string,
  cycle: QuoteCycle,
) =>
  `${currencyCode} ${getCycleProratedAmount(amount, cycle)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`

export const getAmountWithCommas = (amount: number, cycle: QuoteCycle) =>
  `${getCycleProratedAmount(amount, cycle)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`

export const GET_KNOWLEDGE_BASE_URL = gql`
  query KnowledgeBaseUrl($countryCode: String!) {
    knowledgeBaseUrl(countryCode: $countryCode)
  }
`

export const useKnowledgeBaseUrl = (
  countryCode: string,
  options: UseQueryOptions<Record<string, string>, OperationVariables> | undefined,
) => useQuery(GET_KNOWLEDGE_BASE_URL, { variables: { countryCode }, ...options })

export const MANDATORY_COSTS_ORDER: Record<string, number> = {
  "Social Security": 0,
  Allowance: 1,
  Bonus: 2,
  [BENEFIT_GROUP]: 3,
  Accrual: 4,
}

export const sortCostsByGroup = (costs: TaxContribution[]) => {
  const sortedCosts = [...costs].sort((costA, costB) => {
    if (
      costA.group &&
      costB.group &&
      costA.group in MANDATORY_COSTS_ORDER &&
      costB.group in MANDATORY_COSTS_ORDER
    ) {
      return MANDATORY_COSTS_ORDER[costA.group] <= MANDATORY_COSTS_ORDER[costB.group] ? -1 : 1
    } else return 0
  })
  return sortedCosts
}

export const getVariableCompensationGroupTotal = (
  salary: Money,
  variableCompensationGroup?: VariableCompensationsGroup,
): number => {
  if (!variableCompensationGroup) return 0

  const allowances = variableCompensationGroup.allowances
    ? [...variableCompensationGroup.allowances]
    : []
  const bonuses = variableCompensationGroup.bonuses ? [...variableCompensationGroup.bonuses] : []
  const variableCompensations = [...allowances, ...bonuses]

  return (
    variableCompensations.reduce((total: number, compensation) => {
      const value = getVariableCompensationValue(compensation, salary)
      return total + value
    }, 0) || 0
  )
}

export const getBenefitsDetails = (
  currencyCode: string,
  t: TFunction,
  benefits?: BenefitsGroup,
): BenefitsDetails => {
  // Benefits variables
  const provisionedTier = benefits?.benefitsData?.tiers.collection.find(
    tier => tier.id === benefits?.benefitsTier?.id,
  )
  const hasEssentialPackage = provisionedTier?.name === ESSENTIAL_PACKAGE_NAME
  const provisionedAddonIds = benefits?.benefitsAddons?.map(p => p.id) || []
  const addonsPlans = benefits?.benefitsData?.addons?.collection || []
  const hasHealthAddon = addonsPlans.some(addon => addon.category === HEALTH_CATEGORY)
  const provisionedAddons = addonsPlans.filter(a => provisionedAddonIds.includes(a.id))

  // Benefits costs
  const annualBenefitsTierCost = provisionedTier
    ? sumPlanPrices(provisionedTier.includedPlans, currencyCode) * MONTHS_IN_A_YEAR
    : 0

  const annualBenefitsAddonsCost =
    provisionedAddonIds && addonsPlans.length > 0
      ? sumPlanPrices(
          addonsPlans.filter(a => provisionedAddonIds.includes(a.id)),
          currencyCode,
        ) * MONTHS_IN_A_YEAR
      : 0

  const annualBenefitsTierFeeCost =
    provisionedTier && provisionedTier.fee
      ? getBenefitFeeValue(provisionedTier.fee, currencyCode) * MONTHS_IN_A_YEAR
      : 0

  const annualBenefitsPlanFeeCost =
    provisionedAddonIds && provisionedAddons.length > 0
      ? sumPlanFees(provisionedAddons, currencyCode) * MONTHS_IN_A_YEAR
      : 0

  const totalAnnualBenefitsCost = annualBenefitsTierCost + annualBenefitsAddonsCost

  let selectedBenefitPackage: EmployerTaxBenefit | undefined
  let selectedBenefitAddons: TaxContribution | undefined
  let selectedBenefitFee: TaxContribution | undefined
  let selectedPlanFee: TaxContribution | undefined

  if (provisionedTier) {
    selectedBenefitPackage = {
      group: BENEFIT_GROUP,
      amount: annualBenefitsTierCost,
      id: provisionedTier.name,
      name: `Benefits ${provisionedTier.name}`,
    }
  }

  if (provisionedAddonIds.length) {
    selectedBenefitAddons = {
      group: BENEFIT_GROUP,
      amount: annualBenefitsAddonsCost,
      name: t("labelBenefitsAddons"),
    }
  }

  if (annualBenefitsTierFeeCost > 0) {
    selectedBenefitFee = {
      group: BENEFIT_GROUP,
      amount: annualBenefitsTierFeeCost,
      name: t("labelBenefitsFee"),
    }
  }

  if (annualBenefitsPlanFeeCost > 0) {
    selectedPlanFee = {
      group: BENEFIT_GROUP,
      amount: annualBenefitsPlanFeeCost,
      name: t("labelBenefitsAddonsFee"),
    }
  }

  return {
    selectedBenefitPackage,
    selectedBenefitAddons,
    selectedBenefitFee,
    selectedPlanFee,
    hasEssentialPackage,
    hasHealthAddon,
    annualBenefitsAddonsCost,
    annualBenefitsTierFeeCost,
    annualBenefitsPlanFeeCost,
    totalAnnualBenefitsCost,
  }
}

// We need stateName separate because Yup doesn't support tuple validation
export const getValidationSchema = (t: TFunction) => {
  return Yup.object({
    countryCode: Yup.string().trim().required(t("requiredCountry")),
    stateName: Yup.string().when("hasStateOrProvince", {
      is: true,
      then: Yup.string().required(t("requiredState")),
    }),
    salary: Yup.object({
      currencyCode: Yup.string().trim().required(t("requiredCurrency")),
      amount: Yup.string().trim().required(t("requiredSalary")),
    }),
  })
}

export const hasStateOrProvince = (countryCode: string, countryList?: CountryFormData): boolean => {
  return !!countryList?.options?.find(country => country.code === countryCode)?.stateOrProvinces
    .length
}

/**
 * Calculates total salary based on frequency value
 * @param amount
 * @param frequency
 * @returns
 */
export const getTotalAmountByFrequency = (
  amount: number,
  frequency: SalaryFrequencyEnumType,
): number => {
  return frequency === SalaryFrequencyEnumType.Annual ? amount : amount * 12
}

/**
 * Calculates the original amount depending on frequency value and returns as string to fill form field value
 * @param amount is the amount calculated based on frequency as a string
 * @param frequency is either monthly or annual
 * @returns original amount before considering frequency
 */
export const getOriginalAmountByFrequency = (
  amount: number,
  frequency: SalaryFrequencyEnumType,
): number => {
  return frequency === SalaryFrequencyEnumType.Monthly
    ? Math.round(amount / 12)
    : Math.round(amount)
}

/**
 * If country is Spain, display sales percentage first and service within country value if percentage is not provided
 * Else, display service within country if value is "Yes", or sales within country otherwise
 */
export const getVATDisplayValue = (isSpain: boolean, vatParameters: VatParameters) => {
  if (isSpain) {
    if (vatParameters.salesPercentageToCountry !== undefined)
      return `${vatParameters.salesPercentageToCountry}%`
    return vatParameters.worksInCountryOfResidence ? RADIO_LABEL_YES : RADIO_LABEL_NO
  } else if (vatParameters.worksInCountryOfResidence) return RADIO_LABEL_YES
  else if (vatParameters.salesToCountry) return RADIO_LABEL_YES
  else return RADIO_LABEL_NO
}

export const getVATLabel = (
  isSpain: boolean,
  countryName: string,
  t: TFunction,
  vatParameters?: VatParameters | null,
) => {
  if (!vatParameters) return t("vatRequired")

  if (isSpain) {
    return vatParameters.salesPercentageToCountry != null
      ? t("vatSalesPercentage", { countryName })
      : t("vatServices", { countryName })
  } else {
    return vatParameters.salesToCountry != null
      ? t("vatSales", { countryName })
      : t("vatServices", { countryName })
  }
}

export const isCountrySpain = (countryCode: string) => countryCode === "ES"

/** Validate salary fields using MoneyInput */
export const validateSalary = ({ currency, amount }: SalaryInput, errorLabel: string) => {
  return !currency || !amount ? errorLabel : true
}

/** Transform calculation query params to explicitly request
 * oyster fee in the calculation currency (salary currency) */
export const matchFeeCurrencyToCalculation = (calculationQueries: CalculationQueryFields[]) => {
  return calculationQueries.map(calculationQuery => {
    calculationQuery.nativeCurrencyCode = calculationQuery.currencyCode
    return calculationQuery
  })
}
